import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';

// ============= TYPES =============

export type MeetingType = 'discovery' | 'follow_up' | 'proposal' | 'negotiation' | 'kickoff' | 'qbr';
export type MeetingPrepStatus = 'planning' | 'ready' | 'completed' | 'debriefed' | 'cancelled';

export interface AttendeeItem {
  contact_id?: string;
  name: string;
  buying_role?: string;
  key_concern?: string;
}

export interface ConfirmationQuestion {
  question: string;
  confirming: string;
}

export interface NewInfoQuestion {
  question: string;
  learning: string;
  discovery_q?: string;
}

export interface AttitudeQuestion {
  question: string;
  gauging: string;
}

export interface InfoToShare {
  info: string;
  connects_to_concept: string;
  when: 'early' | 'after_probing' | 'if_objection';
}

export interface CredibilityElement {
  type: 'competence' | 'image' | 'track_record' | 'relationship';
  element: string;
  for_whom: string;
}

export interface ObjectionItem {
  objection: string;
  faq_number?: number;
  response_points: string;
}

export interface MeetingPrep {
  id: string;
  opportunity_id: string | null;
  account_id: string;
  meeting_date: string;
  meeting_type: MeetingType;
  our_attendees: string[];
  their_attendees: AttendeeItem[];
  primary_objective: string;
  minimum_acceptable_outcome: string | null;
  advancement_goal: string | null;
  what_they_think_they_need: string | null;
  what_they_actually_need: string | null;
  concept_gap: string | null;
  confirmation_questions: ConfirmationQuestion[];
  new_information_questions: NewInfoQuestion[];
  attitude_questions: AttitudeQuestion[];
  information_to_share: InfoToShare[];
  close_language: string | null;
  credibility_elements: CredibilityElement[];
  anticipated_objections: ObjectionItem[];
  objective_achieved: boolean | null;
  fallback_achieved: boolean | null;
  buying_influence_learnings: string | null;
  new_red_flags: string | null;
  new_strengths: string | null;
  next_action: string | null;
  next_action_owner: string | null;
  debrief_notes: string | null;
  debriefed_at: string | null;
  status: MeetingPrepStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MeetingPrepSummary {
  id: string;
  opportunity_id: string | null;
  account_id: string;
  meeting_date: string;
  meeting_type: MeetingType;
  status: MeetingPrepStatus;
  primary_objective: string;
  our_attendees: string[];
  created_at: string;
  updated_at: string;
  account_name: string;
  opportunity_name: string | null;
  opportunity_stage_id: string | null;
  their_attendee_count: number;
  our_attendee_count: number;
  objective_achieved: boolean | null;
}

export const MEETING_TYPES: { value: MeetingType; label: string }[] = [
  { value: 'discovery', label: 'Discovery Call' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'kickoff', label: 'Kickoff' },
  { value: 'qbr', label: 'QBR' },
];

export const MEETING_PREP_STATUSES: { value: MeetingPrepStatus; label: string; color: string }[] = [
  { value: 'planning', label: 'Planning', color: '#6B7280' },
  { value: 'ready', label: 'Ready', color: '#3B82F6' },
  { value: 'completed', label: 'Completed', color: '#F59E0B' },
  { value: 'debriefed', label: 'Debriefed', color: '#22C55E' },
  { value: 'cancelled', label: 'Cancelled', color: '#EF4444' },
];

export const CREDIBILITY_TYPES = ['competence', 'image', 'track_record', 'relationship'] as const;
export const INFO_TIMING = ['early', 'after_probing', 'if_objection'] as const;

// ============= QUERY HOOKS =============

export function useMeetingPreps(opportunityId: string | undefined | null) {
  return useQuery({
    queryKey: ['meeting-preps', opportunityId],
    queryFn: async (): Promise<MeetingPrepSummary[]> => {
      if (!opportunityId) return [];
      const { data, error } = await supabase
        .from('v_meeting_prep_summary' as any)
        .select('*')
        .eq('opportunity_id', opportunityId)
        .order('meeting_date', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as MeetingPrepSummary[];
    },
    enabled: !!opportunityId,
  });
}

export function useMeetingPrepDetail(id: string | undefined | null) {
  return useQuery({
    queryKey: ['meeting-prep-detail', id],
    queryFn: async (): Promise<MeetingPrep> => {
      if (!id) throw new Error('No meeting prep ID');
      const { data, error } = await supabase
        .from('meeting_prep' as any)
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as unknown as MeetingPrep;
    },
    enabled: !!id,
  });
}

// ============= MUTATION HOOKS =============

export function useCreateMeetingPrep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      opportunity_id?: string | null;
      account_id: string;
      meeting_date: string;
      meeting_type: MeetingType;
      primary_objective?: string;
      advancement_goal?: string | null;
      our_attendees?: string[];
      their_attendees?: AttendeeItem[];
      created_by?: string;
    }) => {
      const { data, error } = await supabase
        .from('meeting_prep' as any)
        .insert({
          opportunity_id: input.opportunity_id || null,
          account_id: input.account_id,
          meeting_date: input.meeting_date,
          meeting_type: input.meeting_type,
          primary_objective: input.primary_objective || '',
          advancement_goal: input.advancement_goal || null,
          our_attendees: input.our_attendees || [],
          their_attendees: input.their_attendees || [],
          created_by: input.created_by || null,
          status: 'planning',
        })
        .select('id')
        .single();
      if (error) throw error;
      return (data as any)?.id as string;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meeting-preps', variables.opportunity_id] });
    },
  });
}

export function useUpdateMeetingPrep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<Omit<MeetingPrep, 'id' | 'created_at'>>) => {
      const { error } = await supabase
        .from('meeting_prep' as any)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['meeting-prep-detail', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['meeting-preps'] });
    },
  });
}

export function useDeleteMeetingPrep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, opportunityId }: { id: string; opportunityId?: string | null }) => {
      const { error } = await supabase
        .from('meeting_prep' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
      return opportunityId;
    },
    onSuccess: (opportunityId) => {
      queryClient.invalidateQueries({ queryKey: ['meeting-preps', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['meeting-prep-detail'] });
    },
  });
}

export function useDebriefMeetingPrep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      opportunityId?: string | null;
      objective_achieved: boolean;
      fallback_achieved?: boolean;
      buying_influence_learnings?: string;
      new_red_flags?: string;
      new_strengths?: string;
      next_action?: string;
      next_action_owner?: string;
      debrief_notes?: string;
    }) => {
      const { id, opportunityId, ...debrief } = input;

      const { error } = await supabase
        .from('meeting_prep' as any)
        .update({
          ...debrief,
          status: 'debriefed',
          debriefed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;

      // Auto-log pipeline activity if opportunity linked
      if (opportunityId) {
        await supabase.from('pipeline_activities' as any).insert({
          opportunity_id: opportunityId,
          activity_type: 'meeting',
          title: `Meeting debriefed — ${debrief.objective_achieved ? 'Objective achieved' : 'Objective not met'}`,
          description: [
            debrief.debrief_notes,
            debrief.new_red_flags ? `Red Flags: ${debrief.new_red_flags}` : null,
            debrief.new_strengths ? `Strengths: ${debrief.new_strengths}` : null,
            debrief.next_action ? `Next: ${debrief.next_action}` : null,
          ].filter(Boolean).join('\n'),
          activity_date: new Date().toISOString(),
          follow_up_date: null,
        });
      }

      return { id, opportunityId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['meeting-prep-detail', result.id] });
      queryClient.invalidateQueries({ queryKey: ['meeting-preps', result.opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-activities', result.opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-board'] });
    },
  });
}

// ============= DOCX GENERATOR =============

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function meetingTypeLabel(type: MeetingType): string {
  return MEETING_TYPES.find((t) => t.value === type)?.label || type;
}

function sectionHeading(text: string): Paragraph {
  return new Paragraph({
    text,
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 300, after: 100 },
    border: {
      bottom: { color: '1F4E79', space: 1, style: BorderStyle.SINGLE, size: 6 },
    },
  });
}

function bodyText(text: string | null | undefined, fallback = '—'): Paragraph {
  return new Paragraph({
    children: [new TextRun({ text: text || fallback, size: 22 })],
    spacing: { after: 80 },
  });
}

function bulletItem(text: string): Paragraph {
  return new Paragraph({
    children: [new TextRun({ text, size: 22 })],
    bullet: { level: 0 },
    spacing: { after: 40 },
  });
}

function labeledField(label: string, value: string | null | undefined): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({ text: `${label}: `, bold: true, size: 22 }),
      new TextRun({ text: value || '—', size: 22 }),
    ],
    spacing: { after: 60 },
  });
}

export async function generateMeetingPrepDocx(
  prep: MeetingPrep,
  accountName: string,
  opportunityName?: string | null
): Promise<void> {
  const children: Paragraph[] = [];

  // Title
  children.push(
    new Paragraph({
      children: [
        new TextRun({ text: 'MEETING PREP — BLUE SHEET', bold: true, size: 32, color: '1F4E79' }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
    })
  );

  // Subtitle
  children.push(
    new Paragraph({
      children: [
        new TextRun({ text: accountName, bold: true, size: 26 }),
        opportunityName ? new TextRun({ text: ` — ${opportunityName}`, size: 26, italics: true }) : new TextRun({ text: '' }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    })
  );

  // Meeting details
  children.push(labeledField('Date', formatDate(prep.meeting_date)));
  children.push(labeledField('Type', meetingTypeLabel(prep.meeting_type)));
  children.push(labeledField('Status', prep.status.toUpperCase()));

  if (prep.our_attendees.length > 0) {
    children.push(labeledField('Our Team', prep.our_attendees.join(', ')));
  }

  if (prep.their_attendees.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Their Attendees:', bold: true, size: 22 })],
        spacing: { before: 80, after: 40 },
      })
    );
    for (const a of prep.their_attendees) {
      const parts = [a.name];
      if (a.buying_role) parts.push(`(${a.buying_role})`);
      if (a.key_concern) parts.push(`— Key concern: ${a.key_concern}`);
      children.push(bulletItem(parts.join(' ')));
    }
  }

  // Section 1: Objectives
  children.push(sectionHeading('1. OBJECTIVES'));
  children.push(labeledField('Primary Objective', prep.primary_objective));
  children.push(labeledField('Minimum Acceptable Outcome', prep.minimum_acceptable_outcome));
  children.push(labeledField('Advancement Goal', prep.advancement_goal));

  // Section 2: Prospect's Concept
  children.push(sectionHeading("2. PROSPECT'S CONCEPT"));
  children.push(labeledField('What They Think They Need', prep.what_they_think_they_need));
  children.push(labeledField('What They Actually Need', prep.what_they_actually_need));
  children.push(labeledField('Concept Gap', prep.concept_gap));

  // Section 3: Questions
  children.push(sectionHeading('3. QUESTIONS PLANNED'));

  if (prep.confirmation_questions.length > 0) {
    children.push(new Paragraph({
      children: [new TextRun({ text: 'Confirmation Questions:', bold: true, size: 22, color: '2E75B6' })],
      spacing: { before: 100, after: 40 },
    }));
    for (const q of prep.confirmation_questions) {
      children.push(bulletItem(`Q: ${q.question}`));
      if (q.confirming) children.push(bodyText(`   Confirming: ${q.confirming}`));
    }
  }

  if (prep.new_information_questions.length > 0) {
    children.push(new Paragraph({
      children: [new TextRun({ text: 'New Information Questions:', bold: true, size: 22, color: '2E75B6' })],
      spacing: { before: 100, after: 40 },
    }));
    for (const q of prep.new_information_questions) {
      children.push(bulletItem(`Q: ${q.question}`));
      if (q.learning) children.push(bodyText(`   Learning: ${q.learning}`));
      if (q.discovery_q) children.push(bodyText(`   Discovery: ${q.discovery_q}`));
    }
  }

  if (prep.attitude_questions.length > 0) {
    children.push(new Paragraph({
      children: [new TextRun({ text: 'Attitude Questions:', bold: true, size: 22, color: '2E75B6' })],
      spacing: { before: 100, after: 40 },
    }));
    for (const q of prep.attitude_questions) {
      children.push(bulletItem(`Q: ${q.question}`));
      if (q.gauging) children.push(bodyText(`   Gauging: ${q.gauging}`));
    }
  }

  // Section 4: Information to Share
  children.push(sectionHeading('4. INFORMATION TO SHARE'));
  if (prep.information_to_share.length > 0) {
    for (const item of prep.information_to_share) {
      children.push(bulletItem(item.info));
      if (item.connects_to_concept) children.push(bodyText(`   Connects to: ${item.connects_to_concept}`));
      children.push(bodyText(`   When: ${item.when.replace('_', ' ')}`));
    }
  } else {
    children.push(bodyText('No information items planned.'));
  }

  // Section 5: Commitment
  children.push(sectionHeading('5. COMMITMENT TO REQUEST'));
  children.push(labeledField('Close Language', prep.close_language));

  // Section 6: Credibility
  children.push(sectionHeading('6. CREDIBILITY ELEMENTS'));
  if (prep.credibility_elements.length > 0) {
    for (const el of prep.credibility_elements) {
      children.push(bulletItem(`[${el.type.replace('_', ' ').toUpperCase()}] ${el.element} — for ${el.for_whom}`));
    }
  } else {
    children.push(bodyText('No credibility elements planned.'));
  }

  // Section 7: Objections
  children.push(sectionHeading('7. ANTICIPATED OBJECTIONS'));
  if (prep.anticipated_objections.length > 0) {
    for (const obj of prep.anticipated_objections) {
      children.push(bulletItem(`Objection: ${obj.objection}`));
      if (obj.faq_number) children.push(bodyText(`   FAQ #${obj.faq_number}`));
      children.push(bodyText(`   Response: ${obj.response_points}`));
    }
  } else {
    children.push(bodyText('No objections anticipated.'));
  }

  // Section 8: Debrief
  if (prep.status === 'debriefed' || prep.status === 'completed') {
    children.push(sectionHeading('8. POST-MEETING DEBRIEF'));
    children.push(labeledField('Objective Achieved', prep.objective_achieved === true ? 'YES' : prep.objective_achieved === false ? 'NO' : '—'));
    children.push(labeledField('Fallback Achieved', prep.fallback_achieved === true ? 'YES' : prep.fallback_achieved === false ? 'NO' : '—'));
    children.push(labeledField('Buying Influence Learnings', prep.buying_influence_learnings));
    children.push(labeledField('New Red Flags', prep.new_red_flags));
    children.push(labeledField('New Strengths', prep.new_strengths));
    children.push(labeledField('Next Action', prep.next_action));
    children.push(labeledField('Next Action Owner', prep.next_action_owner));
    if (prep.debrief_notes) {
      children.push(new Paragraph({
        children: [new TextRun({ text: 'Debrief Notes:', bold: true, size: 22 })],
        spacing: { before: 80, after: 40 },
      }));
      children.push(bodyText(prep.debrief_notes));
    }
  } else {
    children.push(sectionHeading('8. POST-MEETING DEBRIEF (fill after meeting)'));
    children.push(labeledField('Objective Achieved', '☐ Yes  ☐ No'));
    children.push(labeledField('Fallback Achieved', '☐ Yes  ☐ No'));
    children.push(labeledField('Buying Influence Learnings', ''));
    children.push(labeledField('New Red Flags', ''));
    children.push(labeledField('New Strengths', ''));
    children.push(labeledField('Next Action', ''));
    children.push(labeledField('Next Action Owner', ''));
    children.push(labeledField('Debrief Notes', ''));
  }

  // Footer
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Generated ${new Date().toLocaleDateString()} — Miller Heiman Blue Sheet`,
          size: 18,
          color: '999999',
          italics: true,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 400 },
    })
  );

  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: { top: 720, right: 720, bottom: 720, left: 720 },
        },
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  const dateStr = new Date(prep.meeting_date).toISOString().split('T')[0];
  const filename = `Blue_Sheet_${accountName.replace(/[^a-zA-Z0-9]/g, '_')}_${dateStr}.docx`;
  saveAs(blob, filename);
}
